import { Plugin, EditorState, Transaction } from 'prosemirror-state';
import { EditorView, DecorationSet, Decoration } from 'prosemirror-view';
import { DOMSerializer, Slice, Node } from 'prosemirror-model';
import TrackState from '../TrackState';
import { trackedPluginKey, uiStatePluginKey } from './keys';
import parseFragmentForRemoveEl from '../utils/parseFragmentForRemoveEl';

function deletedDom(slice) {
  return function toDom(view) {
    const serializer = DOMSerializer.fromSchema(view.state.schema);

    const htmlFrag = serializer.serializeFragment(slice.content);
    return parseFragmentForRemoveEl(htmlFrag);
  };
}

class TrackedPluginView {
  lastTrackedTimeoutId;
  constructor() {
    this.lastTrackedTimeoutId = null;
  }

  /**
   * On each view update, cancel last timeout if available and start a new one
   * If timeout executes, send message to tracked plugin to cut a new version
   */
  update(view, lastState) {
    if (this.lastTrackedTimeoutId) clearTimeout(this.lastTrackedTimeoutId);

    if (this.shouldCutCommit(view)) {
      this.lastTrackedTimeoutId = setTimeout(() => {
        view.dispatch(view.state.tr.setMeta(trackedPluginKey, { type: 'newCommit' }));
      }, 500);
    }
  }

  destroy() {
    if (this.lastTrackedTimeoutId) clearTimeout(this.lastTrackedTimeoutId);
    this.lastTrackedTimeoutId = null;
  }

  shouldCutCommit(view) {
    const hasUncomittedTrackedSteps = trackedPluginKey.getState(view.state).tracker
      .uncomittedTrackedSteps.length;
    const historyMode = uiStatePluginKey.getState(view.state).historyMode;
    return hasUncomittedTrackedSteps && !historyMode;
  }
}

export default () => {
  const TrackedPlugin = new Plugin({
    view(view) {
      return new TrackedPluginView();
    },
    state: {
      init(config, instance) {
        return {
          tracker: new TrackState([], []),
          decos: DecorationSet.empty
        };
      },
      /*
        Apply each transform to the TrackState instance
        If message is newVersion, create a commit
        If message is compareVersions, build decorations based on diff
      */
      apply(tx, trackState, oldState, newState) {
        const meta = tx.getMeta(trackedPluginKey);
        const newCommit = meta && meta.type === 'newCommit';
        const compareVersion = meta && meta.type === 'compareVersions';
        if (!tx.docChanged && !newCommit && !compareVersion) {
          return trackState;
        }
        let newtrackState =
          tx.docChanged && !compareVersion
            ? trackState.tracker.applyTransform(tx)
            : trackState.tracker;

        if (newCommit) {
          newtrackState = newtrackState.applyCommit(new Date(tx.time));
        }
        const newDecos = [];
        if (compareVersion) {
          let previousDoc = meta.changeSet.config.doc;
          previousDoc = Node.fromJSON(oldState.schema, previousDoc);
          meta.changeSet.changes.forEach((change) => {
            if (change.deleted.length) {
              // Acccumulate offset for changes that have multiple deletion
              //   objs targeting adjacent ranges
              let fromPosOffset = 0;
              change.deleted.forEach((span) => {
                const from = change.fromA + fromPosOffset;
                // Find the content that was removed using the older version's doc
                const deletedSlice = previousDoc.slice(from, from + span.length);
                fromPosOffset += span.length;
                // Add a deccoration widget that inserts a span into the DOM
                newDecos.push(
                  Decoration.widget(change.fromB, deletedDom(deletedSlice), {
                    side: -1
                  })
                );
              });
            }
            change.inserted.forEach((span) => {
              // Apply a mark for added ranges
              newDecos.push(Decoration.inline(change.fromB, change.toB, { class: 'added' }));
            });
          });
        }
        return { tracker: newtrackState, decos: DecorationSet.create(tx.doc, newDecos) };
      }
    },
    key: trackedPluginKey,
    props: {
      decorations(state) {
        // @ts-ignore
        return this.getState(state).decos;
      }
    }
  });

  return TrackedPlugin;
};



// WEBPACK FOOTER //
// ./app/javascript/client/editor-core/plugins/TrackedPlugin.js